import { FC, Fragment, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectAvailableModelRunDetails,
  selectDefaultAvailableModelTypes,
  selectAvailableSegments,
  selectAvailableTimeTypes
} from './mmm-config.selectors';
import { selectSiteId } from 'features/topbar/selectedSiteSlice';
import { useGetDataScienceMmmConfigQuery } from 'app/apis/queryApi';
import {
  selectSelectedModelRunDetails,
  selectSelectedBudgetScenarios,
  selectSelectedSegmentKey,
  selectSelectedTimeType,
  setSelectedModelRunDetails,
  setSelectedBudgetScenario,
  setSelectedSegmentKey,
  setSelectedTimeType,
  selectIsNewBudgetScenarioMessageOpen,
  closeNewBudgetScenarioMessage,
  setBudgetScenarioNameEditMode,
  selectBudgetScenarioIDInNameEditMode,
  selectBudgetScenarioLabel,
  overrideComparedSelectedBudgetScenario
} from './mmm-control.slice';
import DropdownMenu from 'common/components/drop-downs/drop-down-menu';
import DropdownMenuButton from 'common/components/drop-downs/dropdown-menu-button';

import { BudgetScenarioPlannerModel } from '../models/budget-scenario-planner.model';
import { groupBy } from 'common/utils/array-helpers';
import { RootState } from 'app/store';
import {
  changeBudgetScenarioName,
  copyBudgetScenario,
  createSystemBudgetScenarioId,
  deleteBudgetScenario,
  saveInEditModeBudgetScenario,
  selectAllUpdatedBudgetScenario
} from '../edit-budget-scenario.slice';

const {
  isBudgetScenarioPlannerModelsLoading,
  isBudgetScenarioPlannerModelsFetching
} = {
  isBudgetScenarioPlannerModelsLoading: false,
  isBudgetScenarioPlannerModelsFetching: false
};

const MmmControls: FC = () => {
  const dispatch = useDispatch();

  const editedBudgetScenarioPlannerModels = useSelector((state: RootState) =>
    selectAllUpdatedBudgetScenario(state)
  );

  const isNewBudgetScenarioMessageOpen = useSelector(
    selectIsNewBudgetScenarioMessageOpen
  );

  const budgetScenarioIDInNameEditMode = useSelector(
    selectBudgetScenarioIDInNameEditMode
  );

  const [budgetScenarioNameEditValue, setBudgetScenarioNameEditValue] =
    useState<string>('');

  const [isModelRunDetailsDropdownOpen, setModelRunDetailsDropdownOpen] =
    useState(false);

  const [isSegmentSelectorDropdownOpen, setSegmentSelectorDropdownOpen] =
    useState(false);

  const [isTimeRangeSelectorDropdownOpen, setTimeRangeSelectorDropdownOpen] =
    useState(false);

  const [
    isBudgetScenarioSelectorDropdownOpen,
    setBudgetScenarioSelectorDropdownOpen
  ] = useState(false);

  const siteId = useSelector(selectSiteId);
  const selectedSegmentKey = useSelector(selectSelectedSegmentKey);
  const selectedTimeType = useSelector(selectSelectedTimeType);
  const selectedBudgetScenarios = useSelector(selectSelectedBudgetScenarios);
  const selectedModelRunDetails = useSelector(selectSelectedModelRunDetails);

  const budgetScenarioNamesLabel = useSelector(selectBudgetScenarioLabel);

  const {
    isLoading,
    isFetching,
    availableSegments,
    availableTimeTypes,
    availableDefaultModelTypes,
    availableModelRunDetails
  } = useGetDataScienceMmmConfigQuery(
    {
      siteId
    },
    {
      selectFromResult: (result) => {
        const { isLoading, isFetching } = result;
        return {
          ...result,
          isLoading,
          isFetching,
          availableModelRunDetails: selectAvailableModelRunDetails(
            result,
            isFetching
          ),
          availableSegments: selectAvailableSegments(result, isFetching),
          availableTimeTypes: selectAvailableTimeTypes(
            result,
            isFetching,
            selectedSegmentKey
          ),
          availableDefaultModelTypes: selectDefaultAvailableModelTypes(
            result,
            isFetching,
            selectedSegmentKey
          )
        };
      }
    }
  );

  const groupedFullBudgetScenariosForDropdown = useMemo(() => {
    if (
      isBudgetScenarioPlannerModelsLoading ||
      isBudgetScenarioPlannerModelsFetching ||
      !selectedModelRunDetails.modelRunName ||
      selectedModelRunDetails.modelRunName === '' ||
      !selectedTimeType ||
      !selectedSegmentKey
    ) {
      return [];
    }

    const groupedScenarios = groupBy(
      editedBudgetScenarioPlannerModels
        .concat(
          availableDefaultModelTypes.map((modelType, index) => {
            return {
              budgetId: createSystemBudgetScenarioId(
                modelType,
                selectedModelRunDetails.modelRunName!,
                selectedTimeType,
                selectedSegmentKey
              ),
              scenarioName: modelType,
              scenarioType: 'AI Generated',
              modelRunName: selectedModelRunDetails.modelRunName
            } as BudgetScenarioPlannerModel;
          })
        )
        .filter(
          (scenario) =>
            scenario.modelRunName === selectedModelRunDetails.modelRunName
        ),
      (scenario) => scenario.scenarioType
    );

    return Object.entries(
      groupedScenarios as Record<
        'Custom' | 'AI Generated',
        BudgetScenarioPlannerModel[]
      >
    );
  }, [
    selectedModelRunDetails.modelRunName,
    selectedTimeType,
    selectedSegmentKey,
    editedBudgetScenarioPlannerModels,
    availableDefaultModelTypes
  ]);

  const numberOfSelectedScenarios = useMemo(() => {
    if (!selectedBudgetScenarios) {
      return 0;
    }

    return (
      (selectedBudgetScenarios.primarySelectedBudgetScenario ? 1 : 0) +
      (selectedBudgetScenarios.comparedSelectedBudgetScenario ? 1 : 0)
    );
  }, [selectedBudgetScenarios]);

  const orderedAvailableModelRunDetails = useMemo(
    () =>
      availableModelRunDetails.sort(
        (a, b) =>
          new Date(a.targetDate).getTime() - new Date(b.targetDate).getTime()
      ),
    [availableModelRunDetails]
  );

  return (
    <div className="flex flex-col gap-2">
      {isFetching || isLoading ? (
        <p className="text-gray-500">Loading mmm config...</p>
      ) : (
        <>
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium">Available Model Runs:</label>
            {orderedAvailableModelRunDetails.length > 0 ? (
              <DropdownMenu
                isDropdownOpen={isModelRunDetailsDropdownOpen}
                containerClasses="flex justify-between relative mr-3"
                buttonClasses="flex text-sm items-center py-2.5 px-3 justify-between bg-white rounded border border-neutral-200 flex-row-reverse px-3 py-2 bg-white"
                buttonIconClasses="ra-icons-generic-down-arrow text-bottom text-xs flex px-2"
                buttonLabel={
                  !selectedModelRunDetails?.targetDate ||
                  !selectedModelRunDetails.modelRunName
                    ? 'Select Model Run'
                    : `${selectedModelRunDetails.targetDate} ${selectedModelRunDetails.modelRunName}`
                }
                buttonLabelClasses=""
                hasExtraButtonIconClasses={false}
                dropdownMenuClasses="z-40 top-11"
                onDropdownClicked={() =>
                  setModelRunDetailsDropdownOpen(!isModelRunDetailsDropdownOpen)
                }
                onClickAway={() => setModelRunDetailsDropdownOpen(false)}
              >
                {orderedAvailableModelRunDetails.map(
                  ({ modelRunName, targetDate }, index) => (
                    <DropdownMenuButton
                      key={`${targetDate}_${modelRunName}_${index}`}
                      identifier={`model-run-${targetDate}_${modelRunName}`}
                      buttonClasses="flex items-center"
                      onClick={() => {
                        dispatch(
                          setSelectedModelRunDetails({
                            modelRunName,
                            targetDate
                          })
                        );
                        setModelRunDetailsDropdownOpen(false);
                      }}
                    >
                      <span>{`${targetDate} ${modelRunName}`}</span>
                    </DropdownMenuButton>
                  )
                )}
              </DropdownMenu>
            ) : (
              <p className="text-error-300">No Model Runs available</p>
            )}
          </div>
          <div className="flex flex-wrap gap-4">
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium">Available Segments:</label>
              {availableSegments.length > 0 ? (
                <DropdownMenu
                  isDropdownOpen={isSegmentSelectorDropdownOpen}
                  containerClasses="flex justify-between relative w-60 mr-3"
                  buttonClasses="flex text-sm items-center py-2.5 px-3 justify-between bg-white rounded border border-neutral-200 w-60 flex-row-reverse px-3 py-2 bg-white"
                  buttonIconClasses="ra-icons-generic-down-arrow text-bottom text-xs flex"
                  buttonLabel={selectedSegmentKey ?? 'Select Segment'}
                  buttonLabelClasses=""
                  hasExtraButtonIconClasses={false}
                  dropdownMenuClasses="z-40 top-11 w-60"
                  onDropdownClicked={() =>
                    setSegmentSelectorDropdownOpen(
                      !isSegmentSelectorDropdownOpen
                    )
                  }
                  onClickAway={() => setSegmentSelectorDropdownOpen(false)}
                >
                  {availableSegments.map((segmentKey, index) => (
                    <DropdownMenuButton
                      key={`${segmentKey}_${index}`}
                      identifier={`segment-${segmentKey}`}
                      buttonClasses="flex items-center"
                      onClick={() => {
                        dispatch(setSelectedSegmentKey(segmentKey));
                        setSegmentSelectorDropdownOpen(false);
                      }}
                    >
                      <span>{segmentKey}</span>
                    </DropdownMenuButton>
                  ))}
                </DropdownMenu>
              ) : (
                <p className="text-error-300">No segments available</p>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium">
                Available Time Ranges:
              </label>
              {availableTimeTypes.length > 0 ? (
                <DropdownMenu
                  isDropdownOpen={isTimeRangeSelectorDropdownOpen}
                  containerClasses="flex justify-between relative w-60 mr-3"
                  buttonClasses="flex text-sm items-center py-2.5 px-3 justify-between bg-white rounded border border-neutral-200 w-60 flex-row-reverse px-3 py-2 bg-white"
                  buttonIconClasses="ra-icons-generic-down-arrow text-bottom text-xs flex"
                  buttonLabel={selectedTimeType ?? 'Select Time Range'}
                  buttonLabelClasses=""
                  hasExtraButtonIconClasses={false}
                  dropdownMenuClasses="z-40 top-11 w-60"
                  onDropdownClicked={() =>
                    setTimeRangeSelectorDropdownOpen(
                      !isTimeRangeSelectorDropdownOpen
                    )
                  }
                  onClickAway={() => setTimeRangeSelectorDropdownOpen(false)}
                >
                  {availableTimeTypes.map((timeType, index) => (
                    <DropdownMenuButton
                      key={`${timeType}_${index}`}
                      identifier={`time-type-${timeType}`}
                      buttonClasses="flex items-center"
                      onClick={() => {
                        dispatch(setSelectedTimeType(timeType));
                        setTimeRangeSelectorDropdownOpen(false);
                      }}
                    >
                      <span>{timeType}</span>
                    </DropdownMenuButton>
                  ))}
                </DropdownMenu>
              ) : (
                <p className="text-error-300">No Time Ranges available</p>
              )}
            </div>

            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium">
                Available Budget Scenarios:
              </label>
              {groupedFullBudgetScenariosForDropdown.length > 0 ? (
                <>
                  <div className="relative flex items-start gap-4">
                    <DropdownMenu
                      isDropdownOpen={isBudgetScenarioSelectorDropdownOpen}
                      containerClasses="flex justify-between relative w-120 mr-3"
                      buttonClasses="flex text-sm items-center py-2.5 px-3 justify-between bg-white rounded border border-neutral-200 w-120 flex-row-reverse px-3 py-2 bg-white"
                      buttonIconClasses="ml-2 ra-icons-generic-down-arrow text-bottom text-xs flex"
                      buttonLabel={budgetScenarioNamesLabel}
                      buttonLabelClasses=""
                      hasExtraButtonIconClasses={false}
                      dropdownMenuClasses="z-40 top-11 w-120"
                      onDropdownClicked={() =>
                        setBudgetScenarioSelectorDropdownOpen(
                          !isBudgetScenarioSelectorDropdownOpen
                        )
                      }
                      onClickAway={() =>
                        setBudgetScenarioSelectorDropdownOpen(false)
                      }
                    >
                      <>
                        {' '}
                        {groupedFullBudgetScenariosForDropdown.map(
                          (budgetScenarioGroup, index) => {
                            return (
                              <Fragment
                                key={`budget_scenario_selector_group_${index}`}
                              >
                                <div
                                  key={`${budgetScenarioGroup[0]}_${index}`}
                                  className="text-sm pl-1 border-b border-neutral-200 mt-3 text-neutral-500"
                                >
                                  {budgetScenarioGroup[0]}
                                </div>
                                {budgetScenarioGroup[1].map(
                                  (budgetScenario, index) => {
                                    const isChecked =
                                      (selectedBudgetScenarios
                                        .primarySelectedBudgetScenario
                                        ?.budgetId ===
                                        budgetScenario.budgetId &&
                                        selectedBudgetScenarios
                                          .primarySelectedBudgetScenario
                                          ?.scenarioName ===
                                          budgetScenario.scenarioName) ||
                                      (selectedBudgetScenarios
                                        .comparedSelectedBudgetScenario
                                        ?.budgetId ===
                                        budgetScenario.budgetId &&
                                        selectedBudgetScenarios
                                          .comparedSelectedBudgetScenario
                                          ?.scenarioName ===
                                          budgetScenario.scenarioName);

                                    const isDisabled =
                                      !isChecked &&
                                      numberOfSelectedScenarios >= 2;

                                    return (
                                      <DropdownMenuButton
                                        key={`${budgetScenario.scenarioName}_${index}`}
                                        identifier={`model-type-${budgetScenario.scenarioName}`}
                                        buttonClasses="flex items-center w-full gap-2"
                                        onClick={() => {
                                          if (
                                            isDisabled ||
                                            budgetScenarioIDInNameEditMode ===
                                              budgetScenario.budgetId
                                          ) {
                                            return;
                                          }
                                          dispatch(
                                            setSelectedBudgetScenario({
                                              budgetId: budgetScenario.budgetId,
                                              scenarioName:
                                                budgetScenario.scenarioName
                                            })
                                          );
                                        }}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={isChecked}
                                          readOnly
                                        />
                                        {budgetScenarioIDInNameEditMode ===
                                        budgetScenario.budgetId ? (
                                          <>
                                            <input
                                              type="text"
                                              value={
                                                budgetScenarioNameEditValue ??
                                                budgetScenario.scenarioName
                                              }
                                              onClick={(e) => {
                                                e.stopPropagation();
                                              }}
                                              onChange={(e) => {
                                                e.stopPropagation();
                                                setBudgetScenarioNameEditValue(
                                                  e.target.value
                                                );
                                              }}
                                              className="border border-neutral-300 rounded px-2 py-1 w-120"
                                            />
                                            <i
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                dispatch(
                                                  changeBudgetScenarioName({
                                                    budgetId:
                                                      budgetScenario.budgetId,
                                                    scenarioName:
                                                      budgetScenarioNameEditValue
                                                  })
                                                );
                                              }}
                                              className="inline ml-2 ra-icons-generic-checked text-success-400"
                                            />
                                            <i
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setBudgetScenarioNameEditValue(
                                                  budgetScenario.scenarioName
                                                );
                                                dispatch(
                                                  setBudgetScenarioNameEditMode(
                                                    {
                                                      budgetId:
                                                        budgetScenario.budgetId
                                                    }
                                                  )
                                                );
                                              }}
                                              className="inline ml-2 ra-icons-generic-cross-1 text-error-300"
                                            />
                                          </>
                                        ) : (
                                          <>
                                            <span>
                                              {budgetScenario.scenarioName}
                                            </span>
                                            <>
                                              {budgetScenario.scenarioType ===
                                              'Custom' ? (
                                                <>
                                                  <i
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      dispatch(
                                                        saveInEditModeBudgetScenario(
                                                          {
                                                            budgetId:
                                                              budgetScenario.budgetId,
                                                            scenarioName:
                                                              budgetScenario.scenarioName
                                                          }
                                                        )
                                                      );
                                                    }}
                                                    className={`inline ${
                                                      budgetScenario.isSaved
                                                        ? 'text-success-400 ra-icons-generic-checked'
                                                        : 'text-warning-600 ra-icons-generic-check'
                                                    } ml-2`}
                                                    role="button"
                                                  />

                                                  <i
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      setBudgetScenarioNameEditValue(
                                                        budgetScenario.scenarioName
                                                      );
                                                      if (!isChecked) {
                                                        dispatch(
                                                          overrideComparedSelectedBudgetScenario(
                                                            {
                                                              budgetId:
                                                                budgetScenario.budgetId,
                                                              scenarioName:
                                                                budgetScenario.scenarioName
                                                            }
                                                          )
                                                        );
                                                      }

                                                      dispatch(
                                                        setBudgetScenarioNameEditMode(
                                                          {
                                                            budgetId:
                                                              budgetScenario.budgetId
                                                          }
                                                        )
                                                      );
                                                    }}
                                                    className="inline ml-2 ra-icons-generic-edit"
                                                    role="button"
                                                  />
                                                </>
                                              ) : null}
                                            </>
                                            <>
                                              <i
                                                onClick={(e) => {
                                                  e.stopPropagation();

                                                  dispatch(
                                                    copyBudgetScenario({
                                                      budgetId:
                                                        budgetScenario.budgetId
                                                    })
                                                  );
                                                }}
                                                className="ra-icons-generic-copy"
                                                role="button"
                                              />
                                            </>
                                            {budgetScenario.scenarioType ===
                                            'Custom' ? (
                                              <i
                                                onClick={(e) => {
                                                  e.stopPropagation();

                                                  dispatch(
                                                    deleteBudgetScenario({
                                                      budgetId:
                                                        budgetScenario.budgetId
                                                    })
                                                  );
                                                }}
                                                className="ra-icons-generic-bin"
                                                role="button"
                                              />
                                            ) : null}
                                          </>
                                        )}
                                      </DropdownMenuButton>
                                    );
                                  }
                                )}
                              </Fragment>
                            );
                          }
                        )}
                      </>
                    </DropdownMenu>
                    {isNewBudgetScenarioMessageOpen && (
                      <div className="animate-flash left-full ml-4 bg-primary-45 text-primary-800 rounded-md shadow px-4 py-3 w-80 text-sm flex items-start gap-3">
                        <p className="flex-1">
                          Created a new Budget Scenario. Remember to save it
                          before leaving the page.
                        </p>
                        <button
                          onClick={() =>
                            dispatch(closeNewBudgetScenarioMessage())
                          }
                          className="text-primary-800"
                          aria-label="Dismiss"
                        >
                          <i className="text-primary-800 ra-icons-generic-cross-1 w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  {!selectedModelRunDetails ? (
                    <p className="text-error-300">
                      No Budget Scenarios are available for the selected Model
                      Run
                    </p>
                  ) : (
                    <p className="text-warning-600">
                      Please select a Model Run to view Budget Scenarios
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MmmControls;
